import React, { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { X, AlertCircle } from 'lucide-react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useModalScrollLock } from '../hooks/useModalScrollLock'

interface RefundModalProps {
  isOpen: boolean
  payment: {
    id: string
    razorpayPaymentId: string
    totalAmount: number
    paymentStatus: string
  } | null
  isLoading: boolean
  onClose: () => void
  onRefund: (paymentId: string, amount: number) => Promise<void>
}

const refundSchema = z.object({
  refundAmount: z
    .number()
    .positive('Refund amount must be greater than 0')
    .min(0.01, 'Minimum refund amount is ₹0.01'),
})

type RefundFormData = z.infer<typeof refundSchema>

export default function RefundModal({
  isOpen,
  payment,
  isLoading,
  onClose,
  onRefund,
}: RefundModalProps) {
  const [error, setError] = useState<string | null>(null)
  const form = useForm<RefundFormData>({
    resolver: zodResolver(refundSchema),
    mode: 'onBlur',
    defaultValues: {
      refundAmount: 0,
    },
  })

  useEffect(() => {
    if (isOpen) {
      setError(null)
      form.reset({ refundAmount: 0 })
    }
  }, [isOpen, form])

  const refundAmount = form.watch('refundAmount')
  const maxRefundAmount = payment?.totalAmount || 0

  const isValid =
    refundAmount &&
    refundAmount > 0 &&
    refundAmount <= maxRefundAmount

  const handleSubmit = async (data: RefundFormData) => {
    if (!payment) return

    try {
      setError(null)

      if (data.refundAmount > maxRefundAmount) {
        setError(`Refund amount cannot exceed ₹${maxRefundAmount.toFixed(2)}`)
        return
      }

      await onRefund(payment.razorpayPaymentId, data.refundAmount)
      onClose()
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to process refund')
    }
  }

  useModalScrollLock(isOpen)

  if (!isOpen || !payment) return null

  return createPortal(
    <div className="fixed inset-0 bg-black/60 backdrop-blur-xs flex items-center justify-center z-[9999] p-3 sm:p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl border-2 border-[#F6EEE7]">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-gray-900">Refund Payment</h2>
          <button
            onClick={onClose}
            disabled={isLoading}
            className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
          >
            <X size={24} />
          </button>
        </div>

        {/* Payment Info */}
        <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6 rounded">
          <p className="text-sm text-gray-600">
            <span className="font-semibold">Payment ID:</span> {payment.razorpayPaymentId}
          </p>
          <p className="text-sm text-gray-600 mt-1">
            <span className="font-semibold">Total Amount:</span> ₹{payment.totalAmount.toFixed(2)}
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6 rounded flex gap-3">
            <AlertCircle size={20} className="text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {/* Form */}
        <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Refund Amount (₹)
            </label>
            <div className="relative">
              <span className="absolute left-4 top-3 text-gray-500 font-semibold">₹</span>
              <input
                type="number"
                step="0.01"
                min="0.01"
                max={maxRefundAmount}
                placeholder={`Max ₹${maxRefundAmount.toFixed(2)}`}
                disabled={isLoading}
                {...form.register('refundAmount', { valueAsNumber: true })}
                className={`w-full pl-8 pr-4 py-2.5 rounded-lg border-2 transition-all duration-200 outline-none disabled:opacity-50 disabled:cursor-not-allowed ${
                  form.formState.errors.refundAmount
                    ? 'border-red-500 bg-red-50 focus:border-red-600 focus:ring-2 focus:ring-red-200'
                    : 'border-gray-300 bg-white focus:border-[#6D3A0D] focus:ring-2 focus:ring-[#6D3A0D]/20'
                }`}
              />
            </div>
            {form.formState.errors.refundAmount && (
              <p className="mt-1 text-sm text-red-600 font-medium">
                {form.formState.errors.refundAmount.message}
              </p>
            )}
            {refundAmount > 0 && refundAmount <= maxRefundAmount && (
              <p className="mt-1 text-xs text-green-600 font-medium">
                ✓ Refund amount is valid
              </p>
            )}
          </div>

          {/* Buttons */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className="flex-1 px-4 py-2.5 border-2 border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!isValid || isLoading}
              className="flex-1 px-4 py-2.5 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  Processing...
                </>
              ) : (
                'Refund'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  )
}
